import { buildProps, definePropType } from '@ll-plus/utils'

import type { ExtractPropTypes } from 'vue'
import type Calendar from './calendar.vue'


export const calendarEventTextSizes = ['sm', 'md', 'lg'] as const
export const calendarWeekdayFormats = ['min', 'short', 'long'] as const
export const calendarWeekStartDays = [0, 1] as const
export const calendarWeekColumnDateFormats = [
  'M/D',
  'M月D日',
  'D日',
  'D',
] as const
export const calendarFirstDayFormats = [
  'monthDay',
  'dayMonth',
  'chineseDate',
] as const

export const calendarDayTitleFormats = [
  'YYYY-MM-DD',
  'YYYY/MM/DD',
  'YYYY年M月D日',
  'YYYY年MM月DD日',
  'M月D日 dddd',
  'MM/DD ddd',
  'DD/MM/YYYY',
  'ddd, MMM D, YYYY',
  'dddd, MMMM D',
] as const

export const calendarMonthTitleFormats = [
  'YYYY-MM',
  'YYYY年M月',
  'M月',
] as const

export const calendarWeekTitleFormats = [
  'YYYY 第W周',
  'YYYY年 第W周',
  '第W周',
  'W周',
  'YYYY/W',
  'W/YYYY',
  'MM/DD - MM/DD',
  'YYYY/MM/DD - MM/DD',
  'YYYY年M月D日-D日',
  'YYYY年M月D日 - M月D日',
  'Week W, YYYY',
  'YYYY-MM-DD / DD',
] as const

export type CalendarEventTextSize = (typeof calendarEventTextSizes)[number]
export type CalendarWeekdayFormat = (typeof calendarWeekdayFormats)[number]
export type CalendarWeekStartDay = (typeof calendarWeekStartDays)[number]
export type CalendarWeekColumnDateFormat =
  (typeof calendarWeekColumnDateFormats)[number]
export type CalendarFirstDayPresetFormat =
  (typeof calendarFirstDayFormats)[number]
export type CalendarDayTitlePresetFormat =
  (typeof calendarDayTitleFormats)[number]
export type CalendarMonthTitlePresetFormat =
  (typeof calendarMonthTitleFormats)[number]
export type CalendarWeekTitlePresetFormat =
  (typeof calendarWeekTitleFormats)[number]
export type CalendarFirstDayFormat = string
export type CalendarDayTitleFormat = string
export type CalendarMonthTitleFormat = string
export type CalendarWeekTitleFormat = string

export interface CalendarEventDisplayField {
  label?: string
  key: string
  type?: string
  props?: Record<string, any>
  colProps?: Record<string, any>
  style?: Record<string, any>
  on?: Record<string, (...args: any[]) => void>
  editComponent?: {
    type?: string
    label?: string
    props?: Record<string, any>
    colProps?: Record<string, any>
    spanCol?: number
    [key: string]: any
  }
  children?: CalendarEventDisplayField[]
}

/**
 * 日期组配置：将一条原始记录的多组日期字段（如计划/实际）展开为多条派生日程。
 * 详见「日期组功能档案」。
 */
export interface CalendarDateGroup {
  /** 日期组唯一标识，参与复合事件 ID 生成（`${rawRecordId}_${id}`） */
  id: string
  /** 原始记录中「开始时间」对应的字段名 */
  startField: string
  /** 「结束时间」字段名（缺省时事件退化为单点，end = start） */
  endField?: string
  /** 是否启用（默认 true，`enabled !== false` 视为启用） */
  enabled?: boolean
  /** 标签文字（旧字段，作为 tag 的兜底） */
  labelText?: string
  /** 标签颜色（旧字段，作为 eventColor 的兜底） */
  labelColor?: string
  /** 标签文本（新字段，优先级高于 labelText） */
  tag?: string
  /** 标签文本（兼容写法） */
  tagText?: string
  /** 标签背景色 */
  tagColor?: string
  /** 日程统一色调（同时影响左侧边框 + 浅色背景），缺省回退到 labelColor */
  eventColor?: string
  /** 事件背景色，优先级高于 eventColor */
  backgroundColor?: string
  /** 事件边框色，优先级高于 eventColor */
  borderColor?: string
  /** 事件文字颜色 */
  color?: string
  [key: string]: any
}

export type CalendarView = 'month' | 'week' | 'day'
export const defaultCalendarFieldComponents: Record<string, any> = {
  LlTag: 'll-tag',
  LlAvatar: 'll-avatar',
  LlButton: 'll-button',
  LlImage: 'll-image',
  LlSpace: 'll-space',
  LlTooltip: 'll-tooltip',
}

export interface CalendarEvent {
  id?: string | number
  title?: string
  start: string | Date
  end?: string | Date
  /** 事件背景色，优先级高于 eventColor */
  backgroundColor?: string
  /** 事件左侧边框颜色，优先级高于 eventColor */
  borderColor?: string
  /** 事件文字颜色 */
  color?: string
  /** 统一色调：同时设置左侧边框色与浅色背景（backgroundColor/borderColor 会覆盖此值） */
  eventColor?: string
  /** 标题前显示的标签文本 */
  tag?: string
  /** 标签背景颜色 */
  tagColor?: string
  content?: string
  class?: string
  allDay?: boolean
  _eid?: string
  split?: string | number
  /** 派生事件指向的原始记录 ID（日期组展开时写入） */
  rawRecordId?: string
  /** 派生事件所属日期组 ID（日期组展开时写入） */
  _dateGroupId?: string
  [key: string]: any
}

export interface CalendarCellClickRaw {
  date: Date
  split?: string | number
  [key: string]: any
}

export interface CalendarSelectRaw {
  start: Date
  end: Date
  split?: string | number
}

export interface CalendarEventClickRaw {
  event: CalendarEvent
  nativeEvent?: Event
}

export interface CalendarEventContextMenuRaw {
  event: CalendarEvent
  nativeEvent: MouseEvent
}

export interface CalendarEventDropRaw {
  event: CalendarEvent
  originalEvent?: CalendarEvent
  external?: boolean
}

export interface CalendarEventResizeRaw {
  event: CalendarEvent
  originalEvent?: CalendarEvent
}

export interface CalendarEventEditRaw {
  event: CalendarEvent
  originalEvent?: CalendarEvent
  isCreate?: boolean
}

export interface CalendarEventDeleteRaw {
  event: CalendarEvent
}

export interface CalendarDatesChangeRaw {
  view: 'month' | 'week' | 'day'
  startDate: Date
  endDate: Date
  firstCellDate?: Date
  lastCellDate?: Date
  events?: CalendarEvent[]
}

export const calendarProps = buildProps({
  modelValue: {
    type: definePropType<CalendarEvent[]>(Array),
    default: () => [],
  },
  view: {
    type: definePropType<CalendarView>(String),
    default: 'month',
  },
  currentDate: {
    type: definePropType<string | Date>([String, Date]),
    default: undefined,
  },
  editableEvents: {
    type: definePropType<Partial<{
      drag: boolean
      resize: boolean
      delete: boolean
      create: boolean
      selectCreate: boolean
    }>>(Object),
    default: () => ({}),
  },
  selectable: {
    type: Boolean,
    default: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  disableCreateInPast: {
    type: Boolean,
    default: false,
  },
  clickOutOfMonthJump: {
    type: Boolean,
    default: false,
  },
  allDaySlot: {
    type: Boolean,
    default: false,
  },
  nowIndicator: {
    type: Boolean,
    default: true,
  },
  timeSlotFormat: {
    type: String,
    default: 'HH:mm',
  },
  dayMaxEvents: {
    type: definePropType<boolean | number>([Boolean, Number]),
    default: true,
  },
  eventsOnMonthView: {
    type: definePropType<boolean | 'short'>([Boolean, String]),
    default: true,
  },
  height: {
    type: definePropType<string | number>([String, Number]),
    default: 'auto',
  },
  slotMinTime: {
    type: String,
    default: '00:00:00',
  },
  slotMaxTime: {
    type: String,
    default: '24:00:00',
  },
  slotDuration: {
    type: String,
    default: '00:15:00',
  },
  timeCellHeight: {
    type: Number,
    default: 190,
  },
  autoScrollToNow: {
    type: Boolean,
    default: true,
  },
  autoScrollOnlyToday: {
    type: Boolean,
    default: true,
  },
  eventTitleKey: {
    type: String,
    default: 'title',
  },
  eventDescriptionKey: {
    type: String,
    default: 'content',
  },
  eventImageKey: {
    type: String,
    default: '',
  },
  eventMetaFields: {
    type: definePropType<CalendarEventDisplayField[]>(Array),
    default: () => [],
  },
  eventDisplayFields: {
    type: definePropType<CalendarEventDisplayField[]>(Array),
    default: () => [],
  },
  components: {
    type: definePropType<Record<string, any>>(Object),
    default: () => ({}),
  },
  eventMaxMetaCount: {
    type: Number,
    default: 2,
  },
  eventTextSize: {
    type: definePropType<CalendarEventTextSize>(String),
    default: 'md',
    values: calendarEventTextSizes,
  },
  weekdayFormat: {
    type: definePropType<CalendarWeekdayFormat>(String),
    default: 'long',
    values: calendarWeekdayFormats,
  },
  weekStartDay: {
    type: definePropType<CalendarWeekStartDay>(Number),
    default: 0,
    values: calendarWeekStartDays,
  },
  weekColumnDateFormat: {
    type: definePropType<CalendarWeekColumnDateFormat>(String),
    default: 'D',
    values: calendarWeekColumnDateFormats,
  },
  firstDayFormat: {
    type: definePropType<CalendarFirstDayFormat>(String),
    default: 'chineseDate',
  },
  monthTitleFormat: {
    type: definePropType<CalendarMonthTitleFormat>(String),
    default: 'YYYY-MM',
  },
  weekTitleFormat: {
    type: definePropType<CalendarWeekTitleFormat>(String),
    default: 'YYYY 第W周',
  },
  dayTitleFormat: {
    type: definePropType<CalendarDayTitleFormat>(String),
    default: 'YYYY-MM-DD',
  },
  showLunar: {
    type: Boolean,
    default: false,
  },
  /**
   * 日期组配置：将一条原始记录展开为多条派生日程（如「计划」「实际」）。
   * 为空数组时功能关闭，组件按常规事件渲染 modelValue。
   */
  dateGroups: {
    type: definePropType<CalendarDateGroup[]>(Array),
    default: () => [],
  },
  /**
   * 派生事件标题模板，支持 `{字段名}` 占位符（如 `{name}-{stage}`）。
   * 为空时回退到原始记录的 title。
   */
  dateGroupTitleFormat: {
    type: String,
    default: '',
  },

} as const)

export type CalendarProps = ExtractPropTypes<typeof calendarProps>
export type CalendarInstance = InstanceType<typeof Calendar>

export interface CalendarChangeContext {
  events: CalendarEvent[]
}

export interface CalendarDateClickContext extends CalendarChangeContext {
  raw: CalendarCellClickRaw
}

export interface CalendarSelectContext extends CalendarChangeContext {
  raw: CalendarSelectRaw
}

export interface CalendarEventClickContext extends CalendarChangeContext {
  raw: CalendarEventClickRaw
  event: CalendarEvent
}

export interface CalendarEventDropContext extends CalendarChangeContext {
  raw: CalendarEventDropRaw
  event: CalendarEvent
}

export interface CalendarEventContextMenuContext extends CalendarChangeContext {
  raw: CalendarEventContextMenuRaw
  event: CalendarEvent
}

export interface CalendarEventResizeContext extends CalendarChangeContext {
  raw: CalendarEventResizeRaw
  event: CalendarEvent
}

export interface CalendarEventEditContext extends CalendarChangeContext {
  raw: CalendarEventEditRaw
  event: CalendarEvent
}

export interface CalendarEventDeleteContext extends CalendarChangeContext {
  raw: CalendarEventDeleteRaw
  event: CalendarEvent
}

export interface CalendarDatesChangeContext extends CalendarChangeContext {
  raw: CalendarDatesChangeRaw
}

function formatCalendarDateTime(value: Date) {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  const hours = String(value.getHours()).padStart(2, '0')
  const minutes = String(value.getMinutes()).padStart(2, '0')
  const seconds = String(value.getSeconds()).padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
}

export function normalizeCalendarEvent(event: CalendarEvent): CalendarEvent {
  const normalized = { ...event }

  if (normalized.start instanceof Date) {
    normalized.start = formatCalendarDateTime(normalized.start)
  }

  if (normalized.end instanceof Date) {
    normalized.end = formatCalendarDateTime(normalized.end)
  }

  return normalized
}

export function normalizeCalendarEvents(events?: CalendarEvent[]): CalendarEvent[] {
  return (events ?? []).map(normalizeCalendarEvent)
}

export const titleEnum = {
  day:'日',
  week:'周',
  month:'月',
}